import { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import UiTag from "../../../components/tag/Tag.component";
import { Formatter } from "../../../services/formatter/formatter.service";
import UiPhoneCaller from "../../../components/phone-caller/PhoneCaller.component";
import { ParticipantContextProps } from "../../../models/contexts/participant-context.model";
import { ParticipantContext } from "../../../contexts/Participant.context";
import { HelperService } from "../../../services/helpers/helper.service";
import ParticipantHedisGoals from "./ParticipantHedisGoals";
import { useProvidersInfo } from "../../../hooks/useProvidersInfo";
import { ProviderMinimalInfo } from "../../../models/entities/provider.model";
import ParticipantProfileAppointment from "./ParticipantProfileAppointment";
import { PreferredPronouns, Timezone } from "../../../models/entities/participant.model";
import ParticipantInstitutionDetails from "./ParticipantInstitutionDetails";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import UiModalNextReachoutDate from "../../../components/modal-next-reachout-date/ModalNextReachoutDate.component";
import { UiModalNextReachoutDateProps } from "../../../models/components/modal-next-reachout-date.model";
import { EhrProvider } from "../../../models/entities/ehr-provider.model";

interface ParticipantProfileInfoProps {
	onRefresh: () => Promise<void>;
	/** Called when EHR providers are loaded; used to pass them to EhrMrn via context */
	onEhrProvidersLoad?: (providers: EhrProvider[]) => void;
}

const ParticipantProfileInfo = ({ onRefresh, onEhrProvidersLoad }: ParticipantProfileInfoProps) => {
	const { t } = useTranslation("common");
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);
	const participantProfile = participantContext.participant!;

	// Next reachout date modal state
	const [nextReachoutDateModal, setNextReachoutDateModal] =
		useState<UiModalNextReachoutDateProps>({
			isVisible: false,
			initialDate: participantProfile?.nextReachoutDate
				? new Date(participantProfile.nextReachoutDate.replace("Z", ""))
				: null,
			onComplete: async (date: Date | null) => {
				const body: { nextReachoutDate: string | null } = {
					nextReachoutDate: date
						? Formatter.dateUTCWithoutTime(date)!.toISOString()
						: null,
				};

				await EndpointsService.dataRetriever
					.updateProfileGeneralInfo({
						body: {
							participantId: participantProfile.id,
							...body,
						},
					})
					.catch((error) => {
						console.error("Error updating participant next reachout date:", error);
					})
					.finally(() => {
						onRefresh();
						setNextReachoutDateModal({ ...nextReachoutDateModal, isVisible: false });
					});
			},
			onCancel: () => {
				setNextReachoutDateModal({ ...nextReachoutDateModal, isVisible: false });
			},
		});

	// Sync modal initial date with context updates
	useEffect(() => {
		if (participantProfile?.nextReachoutDate) {
			// Don't adjust weekend dates - use the actual date from participant profile
			const date = new Date(participantProfile.nextReachoutDate.replace("Z", ""));
			setNextReachoutDateModal({
				...nextReachoutDateModal,
				initialDate: date,
			});
		} else {
			setNextReachoutDateModal({
				...nextReachoutDateModal,
				initialDate: null,
			});
		}
	}, [participantProfile?.nextReachoutDate]);

	const fullName = Formatter.fullName(
		participantProfile.firstName,
		participantProfile.lastName,
		participantProfile.preferredName
	);
	const status = Formatter.statusTag(participantProfile.status);
	const contactPurpose = HelperService.formatLastContactPurpose(
		participantProfile.contactPurpose
	);
	const email = participantProfile.emails?.find((p) => p.isPrimary)?.emailAddress ?? null;
	const phoneNumber = participantProfile.phones?.length
		? participantProfile.phones.find((p) => p.isPrimary)
		: null;
	const coaches = useProvidersInfo();

	const coachFullName = (coach?: ProviderMinimalInfo) => {
		let result = "";
		if (coach) {
			result = Formatter.fullName(coach.firstName, coach.lastName);
		} else {
			result = Formatter.NO_DATA;
		}
		return result;
	};

	const openNextReachoutDateModal = () => {
		// Don't adjust weekend dates - use the actual date from participant profile
		const currentDate = participantProfile?.nextReachoutDate
			? new Date(participantProfile.nextReachoutDate.replace("Z", ""))
			: null;
		setNextReachoutDateModal({
			...nextReachoutDateModal,
			isVisible: true,
			initialDate: currentDate,
		});
	};

	// Fetch all EHR providers here; pass to parent so it can provide to EhrMrn via context
	const { data: allEhrProvidersData } = useQuery({
		queryKey: ["all-ehr-providers"],

		queryFn: async () => {
			return await EndpointsService.dataRetriever.getAllEhrProviders();
		},
	});
	useEffect(() => {
		if (allEhrProvidersData) onEhrProvidersLoad?.(allEhrProvidersData);
	}, [allEhrProvidersData, onEhrProvidersLoad]);

	// All EHR providers from context (set by parent when we call onEhrProvidersLoad)
	const allEhrProviders = participantContext.allEhrProviders;
	const elationProvider = allEhrProviders?.find((p) => p.name?.toLowerCase() === "elation");
	const elationParticipant = participantProfile?.ehrProviderParticipants?.find(
		(epp) => epp.ehrProviderId === elationProvider?.id
	);
	const elationHref =
		elationProvider?.baseUrl && elationParticipant?.providerParticipantId
			? `${elationProvider.baseUrl.replace(/\/$/, "")}/patient/${elationParticipant.providerParticipantId}`
			: null;

	return (
		<div className="participant-profile">
			{/* Primary info */}
			<div className="primary-info">
				{/* Primary info: first line */}
				<div className="primary-info__first">
					{/* Full name */}
					{fullName && <div className="name">{fullName}</div>}

					{/* Pronouns */}
					{participantProfile.pronouns &&
						participantProfile.pronouns !== PreferredPronouns.PREFER_NOT && (
							<div className="pronouns">{`(${t(`ENUMS.PRONOUNS.${participantProfile.pronouns}`)})`}</div>
						)}

					{/* Brand */}
					<img
						alt="brand logo"
						className="logo"
						src={participantProfile.brandLogo}
					/>
				</div>

				{/* Primary info: second line */}
				<div className="primary-info__second">
					{/* Status (user_state) */}
					{status && <UiTag {...status} />}

					{/* Not covered */}
					{!participantProfile.isCovered && (
						<span className="text-bold">
							{t("PARTICIPANT.PROFILE.FIELDS.NOT_COVERED")}
						</span>
					)}

					{/* Appointments */}
					<ParticipantProfileAppointment participantContext={participantContext} />

					{/* Phone number */}
					{phoneNumber?.countryCode && phoneNumber.phoneNumber ? (
						<UiPhoneCaller
							phoneNumber={`${phoneNumber?.countryCode}${phoneNumber.phoneNumber}`}
							name={`${participantProfile.firstName} ${participantProfile.lastName}`}
						/>
					) : (
						<i>{t("PARTICIPANT.PROFILE.FIELDS.NO_PHONE_NUMBER")}</i>
					)}
				</div>
			</div>

			{/* Secondary info */}
			<div className="secondary-info row">
				<div className="col-12 col-sm-4 col-md-4 col-lg-4 col-xl-4 col-xxl-4">
					<div className="row">
						{/* Kannact ID */}
						<div className="col-6 col-sm-12">{participantProfile.kannactId}</div>

						{/* DOB: Date Of Birth */}
						<div className="col-6 col-sm-12">
							{participantProfile?.dob ? (
								t("PARTICIPANT.PROFILE.FIELDS.DOB", {
									value: Formatter.dateISOToString(participantProfile.dob, {
										day: "numeric",
										month: "short",
										year: "numeric",
										timeZone: "UTC",
									}),
									age: Formatter.calculateAge(new Date(), participantProfile.dob),
								})
							) : (
								<i>{t("PARTICIPANT.PROFILE.FIELDS.NO_DOB")}</i>
							)}
						</div>

						{/* Assigned coach */}
						{coaches.data && (
							<div className="col-6 col-sm-12">
								{participantProfile?.coachId ? (
									coachFullName(
										coaches.data.find(
											(x) => x.coachId === participantProfile.coachId
										)
									)
								) : (
									<i>{t("PARTICIPANT.PROFILE.FIELDS.NO_ASSIGNED_COACH")}</i>
								)}
							</div>
						)}

						<div className="col-6 col-sm-12">
							{elationHref ? (
								<a
									href={elationHref}
									target="_blank"
									rel="noopener noreferrer"
									style={{ textDecoration: "underline" }}
								>
									{t("PARTICIPANT.PROFILE.FIELDS.ELATION")}
								</a>
							) : (
								<i>{t("PARTICIPANT.PROFILE.FIELDS.NO_ELATION")}</i>
							)}
						</div>

						{/* Institution details */}
						<div className="col-6 col-sm-12">
							<ParticipantInstitutionDetails
								participantContext={participantContext}
							/>
						</div>
					</div>
				</div>

				<div className="col-12 col-sm-4 col-md-3 col-lg-3 col-xl-3 col-xxl-3">
					<div className="row">
						{/* Time zone */}
						<div className="col-6 col-sm-12">
							{!participantProfile.timezone ||
							participantProfile.timezone === Timezone.NA ? (
								<i>{t("PARTICIPANT.PROFILE.FIELDS.NO_TIMEZONE")}</i>
							) : (
								participantProfile.timezone
							)}
						</div>

						{/* Language */}
						<div className="col-6 col-sm-12">
							{participantProfile?.language ? (
								t(`ENUMS.LANGUAGE.${participantProfile.language}`)
							) : (
								<i>{t("PARTICIPANT.PROFILE.FIELDS.NO_LANGUAGE")}</i>
							)}
						</div>

						{/* Gender */}
						<div className="col-6 col-sm-12">
							{participantProfile?.gender === "M" ||
							participantProfile?.gender === "F" ? (
								t(Formatter.gender(participantProfile.gender))
							) : (
								<i>{t("PARTICIPANT.PROFILE.FIELDS.NO_GENDER")}</i>
							)}
						</div>

						{/* Contact purpose */}
						<div className="col-6 col-sm-12">
							{!participantProfile.contactPurpose ||
							!contactPurpose ||
							participantProfile.contactPurpose === "NONE" ? (
								<i>{t("PARTICIPANT.PROFILE.FIELDS.NO_REACH_OUT")}</i>
							) : (
								(t(contactPurpose.key, contactPurpose.params) as string)
							)}
						</div>

						{/* Next contact date purpose */}
						<div className="col-6 col-sm-12 next-reachout-date-calendar">
							<span>{"Next RO: "}</span>
							<span
								style={{
									cursor: "pointer",
									display: "inline-flex",
									alignItems: "center",
									gap: "0.25rem",
								}}
								onClick={openNextReachoutDateModal}
							>
								{participantProfile?.nextReachoutDate
									? Formatter.dateISOToString(
											participantProfile.nextReachoutDate,
											{
												day: "2-digit",
												month: "2-digit",
												year: "2-digit",
												timeZone: "UTC",
											}
										)!
									: ""}
								<span
									className="pi pi-pencil"
									style={{ fontSize: "14px", marginLeft: "0.25rem" }}
								></span>
							</span>
						</div>
					</div>
				</div>

				<div className="col-12 col-sm-4 col-md-5 col-lg-5 col-xl-5 col-xxl-5">
					<div className="row">
						{/* Email */}
						<div className="col-12">
							{email ?? <i>{t("PARTICIPANT.PROFILE.FIELDS.NO_EMAIL")}</i>}
						</div>

						{/* Coaching method */}
						<div className="col-6 col-sm-12">
							{t("PARTICIPANT.PROFILE.FIELDS.COACHING_METHOD", {
								value: t(
									participantProfile.preferredCoachingMethod
										? Formatter.preferredCoachingMethod(
												participantProfile.preferredCoachingMethod
											)
										: Formatter.NO_DATA
								),
							})}
						</div>

						{/* Whole health */}
						<div className="col-6 col-sm-12">
							{participantProfile.shippingAddressState ?? (
								<i>{t("PARTICIPANT.PROFILE.FIELDS.NO_STATE")}</i>
							)}
						</div>

						{/* Hedis goals */}
						<div className="col-6 col-sm-12">
							<ParticipantHedisGoals participantContext={participantContext} />
						</div>
					</div>
				</div>
			</div>

			{/* Next reachout date modal */}
			{nextReachoutDateModal.isVisible && (
				<UiModalNextReachoutDate {...nextReachoutDateModal} />
			)}
		</div>
	);
};

export default ParticipantProfileInfo;
